/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
	Drawer, Stack, Typography, Button, Accordion, AccordionSummary,
	AccordionDetails, FormGroup, Checkbox, Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MdClose } from 'react-icons/md';
import { LanguageContext } from '../../../../../../libs/Context';
import { getEvents } from '../../../../../actions/recognition_actions';
import { useDispatch, useSelector } from 'react-redux';
import { catchErr, queryParams } from '../../../../../../libs/HelperContent';
import { setEventsData } from '../../../../../../redux/NewsFeedRedux';
import { feedActionContext } from '../../../..';

const drawerStyles = {
	width         : { xs: '100%', sm: '70%', md: '30%', lg: '30%' },
	display       : 'flex',
	flexDirection : 'column'
};

const headerStyles = {
	position        : 'sticky',
	top             : 0,
	zIndex          : 1,
	backgroundColor : '#F1F2F6',
	borderBottom    : '1px solid #e0e0e0'
};

const footerStyles = {
	position        : 'sticky',
	bottom          : 0,
	backgroundColor : '#fff',
	borderTop       : '1px solid #e0e0e0'
};

interface EventFilterProps {
  isDrawerOpen: boolean;
  handleClose: () => void;
}

interface EventData {
  limit: number;
  skip: number;
  isLoading: boolean;
  eData: any[];
}

const EVENT_TYPES = [
	{ eventType: 1, label: 'Recognition' },
	{ eventType: 2, label: 'Birthday' },
	{ eventType: 3, label: 'Wedding Anniversary' },
	{ eventType: 4, label: 'Work Anniversary' }

];

const EventsFilter: React.FC<EventFilterProps> = ({ isDrawerOpen, handleClose }) =>
{
	const session = useSelector((state: any) => state?.mysession?.session);
	const dispatch = useDispatch();
	const { language } = useContext(LanguageContext);
	const { setFeedCardActn } = useContext(feedActionContext);

	const queryParamRes = useMemo(() =>
		queryParams(
			session?.userId,
			session?.companyId,
			session?.empRole,
			session?.role,
			'get'
		),
	[ session ]
	);

	const [ selectedOptions, setSelectedOptions ] = useState<Record<string, number[]>>({});
	const [ eventData, setEventData ] = useState<EventData>({
		limit     : 50,
		skip      : 0,
		isLoading : false,
		eData     : []
	});

	const getEventsData = useCallback(async () =>
	{
		const currentDate = new Date().setHours(0, 0, 0, 0);
		const query = `?limit=${eventData.limit}&skip=${eventData.skip}&timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}&currentDate=${currentDate}${queryParamRes}`;

		if (!eventData.isLoading)
		{
			try
			{
				setEventData((prev) => ({ ...prev, isLoading: true }));

				const res = await getEvents(query);

				if (res?.status)
				{
					setEventData((prev) => ({
						...prev,
						eData     : res.data ?? [],
						isLoading : false
					}));
				}
				else
				{
					console.error('Failed to fetch events');
				}
			}
			catch (error)
			{
				catchErr(error);
				setEventData((prev) => ({ ...prev, isLoading: false }));
			}
		}
	}, [ eventData.limit, eventData.skip, queryParamRes ]);

	const handleCheckbox = useCallback((key: string, value: number) =>
	{
		setSelectedOptions((prev) =>
		{
			const currentValues = prev[key] || [];
			const newValues = currentValues.includes(value)
				? currentValues.filter((item) => item !== value)
				: [ ...currentValues, value ];

			return { ...prev, [key]: newValues };
		});
	}, []);

	const handleApply = useCallback(() =>
	{
	// If no event types are selected, return all events
		if (!selectedOptions.eventCards || selectedOptions.eventCards.length === 0)
		{
			dispatch(setEventsData(eventData.eData)); // Add Redux dispatch
			handleClose();

			return;
		}

		// Filter events based on selected event types
		const filteredEvents = eventData.eData.filter((event) =>
			selectedOptions.eventCards.includes(event.eventType)
		);

		// Update Redux
		setFeedCardActn([ 'allEventCmnt', filteredEvents[0] ]);
		dispatch(setEventsData(filteredEvents));
		console.log('setEventData02 ---------------');

		handleClose();
	}, [ eventData.eData, selectedOptions, dispatch, handleClose ]);

	const handleParentClose = useCallback((e: React.SyntheticEvent, reason: string) =>
	{
		if (reason !== 'backdropClick')
		{
			handleClose();
		}
	}, [ handleClose ]);

	useEffect(() =>
	{
		getEventsData();
	}, [ getEventsData ]);

	return (
		<Drawer
			anchor='right'
			open={isDrawerOpen}
			onClose={handleParentClose}
			PaperProps={{ sx: drawerStyles }}
		>
			<Stack
				direction='row'
				alignItems='center'
				p={1}
				bgcolor='#F1F2F6'
				justifyContent='space-between'
				sx={headerStyles}
			>
				<Typography variant='h6' pl={1}>
					{language?.filter || 'Filter'}
				</Typography>
				<Stack direction='row' alignItems='center'>
					{Object.keys(selectedOptions).length > 0 && (
						<Button
							onClick={() => setSelectedOptions({})}
							variant='text'
							size='small'
							sx={{ textTransform: 'none', fontSize: '16px' }}
						>
							{language?.reset || 'Reset'}
						</Button>
					)}
					<MdClose onClick={handleClose} style={{ fontSize: '20px', cursor: 'pointer' }} />
				</Stack>
			</Stack>

			<Box style={{ flex: 1, overflowY: 'auto' }}>
				<Accordion
					disableGutters
					elevation={0}
					sx={{
						'&:before'   : { display: 'none' },
						borderBottom : '1px solid #e0e0e0',
						width        : '100%'
					}}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						sx={{
							display         : 'flex',
							justifyContent  : 'space-between',
							height          : '54px',
							margin          : '0 !important',
							backgroundColor : '#E7F6FA'
						}}
					>
						<Typography variant='body1' fontWeight={500}>
							Event Cards
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<FormGroup>
							{EVENT_TYPES.map((option) => (
								<Box key={option.eventType} sx={{ display: 'flex', alignItems: 'center' }}>
									<Checkbox
										name='eventCards'
										checked={selectedOptions.eventCards?.includes(option.eventType) || false}
										onChange={() => handleCheckbox('eventCards', option.eventType)}
									/>
									<Typography variant='body1'>{option.label}</Typography>
								</Box>
							))}
						</FormGroup>
					</AccordionDetails>
				</Accordion>
			</Box>

			<Stack
				direction='row'
				spacing={2}
				justifyContent='flex-end'
				width='100%'
				p={3}
				sx={footerStyles}
			>
				<Button variant='outlined' sx={{ textTransform: 'none' }} onClick={handleClose}>
					{language?.cancel || 'Cancel'}
				</Button>
				<Button
					variant='contained'
					sx={{ color: '#ffffff', textTransform: 'none' }}
					onClick={handleApply}
				>
					{language?.apply || 'Apply'}
				</Button>
			</Stack>
		</Drawer>
	);
};

export default React.memo(EventsFilter);